import { createContext, useContext } from "react";
import type { Permissions, Uuid } from "../api";

/**
 * Le portefeuille que les écrans métier regardent, et ce qu'on a le droit d'y
 * faire.
 *
 * Pour une entreprise, c'est le sien, posé par `EntrepriseLayout` depuis la
 * session. Pour un commercial qui consulte un client, c'est celui de
 * l'entreprise de l'URL, posé par `EsnEntrepriseLayout`. Les six écrans
 * n'ont pas à savoir lequel des deux : ils lisent `useWalletID()`.
 */
export type WalletScope = {
  walletId: Uuid;
  companyId: Uuid;
  /** Droits du principal sur CE portefeuille. */
  permissions: Permissions;
  /** Où mènent les gestes de l'écran, selon l'espace où il est monté. */
  links: WalletLinks;
};

/**
 * Les chemins vers lesquels les écrans métier renvoient. Ils diffèrent entre
 * l'espace entreprise (`/commande`) et la fiche d'un client vue par l'ESN
 * (`/esn/entreprises/:id/commande`) ; `achat` est nul quand le principal ne
 * peut pas acheter.
 */
export type WalletLinks = {
  commande: string;
  achat: string | null;
};

export const WalletContext = createContext<WalletScope | null>(null);

/** Le portefeuille courant et ses droits. */
export function useWalletScope(): WalletScope {
  const ctx = useContext(WalletContext);
  if (ctx === null) {
    throw new Error("useWalletScope doit être appelé sous un WalletProvider.");
  }
  return ctx;
}

/** Le seul identifiant que les écrans métier passent à l'API. */
export function useWalletID(): Uuid {
  return useWalletScope().walletId;
}
